import { useCallback, useEffect, useRef, useState } from "react";
import {
  getAntiForgeryToken,
  getResidenceType,
  ResidenceTypeModel,
  uploadImageForResidenceType,
} from "../../clients/BackOfficeApi";
import { Box, Flex, Heading, Spinner, Text } from "@chakra-ui/react";
import { useParams } from "react-router-dom";
import { useIsAuthenticated } from "@azure/msal-react";
import {
  cachedImagesAtom,
  ResidenceTypeImage,
} from "../../elements/ResidenceTypeImage";
import { AttachmentIcon } from "@chakra-ui/icons";
import { useAtom } from "jotai";
import { ImageInput } from "../../components/ImageInput";
import { DetailPageHeader } from "../../compositions/DetailPageHeader";
import { ResidenceTypeActions } from "../../components/ResidenceTypeContent/ResidenceTypeActions";
import { SituationTable } from "../../components/SituationContent/SituationTable";
import { AssignedCustomers } from "../../components/ResidenceTypeContent/AssignedCustomers";
import { VisibilityIcon } from "../../elements/VisibilityIcon";

export function ResidenceTypeDetailPage() {
  const residenceTypeId = Number(useParams().residenceTypeId);
  const [error, setError] = useState<string>();
  const isAuthenticated = useIsAuthenticated();
  const [, setCachedImages] = useAtom(cachedImagesAtom);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [loading, setLoading] = useState(true);

  const [residenceType, setResidenceType] = useState<ResidenceTypeModel>();

  const fetchResidenceType = useCallback(() => {
    if (!isAuthenticated) return;

    setLoading(true);

    const abortController = new AbortController();

    if (!Number.isInteger(residenceTypeId) || residenceTypeId <= 0) {
      setError("Ongeldig woningtype ID");
      setLoading(false);
      return;
    }

    getResidenceType({
      path: { residenceTypeId },
      signal: abortController.signal,
      throwOnError: true,
    })
      .then(({ data }) => {
        setResidenceType(data);
      })
      .catch((e) => {
        if (abortController.signal.aborted) return;

        setError(`Fout bij ophalen woningtype: ${e.message ?? String(e)}`);
      })
      .finally(() => setLoading(false));

    return () => abortController.abort("Component unmounted or re-rendered");
  }, [isAuthenticated, residenceTypeId]);

  useEffect(() => fetchResidenceType(), [fetchResidenceType]);

  const uploadResidenceTypeImage = useCallback(
    (file: File | undefined) => {
      if (!file || !residenceTypeId) return;

      setCachedImages((c) => ({
        ...c,
        [residenceTypeId]: URL.createObjectURL(file),
      }));

      return getAntiForgeryToken({ credentials: "include", throwOnError: true })
        .then(({ data: xsrfToken }) =>
          uploadImageForResidenceType({
            path: { residenceTypeId },
            body: { file },
            headers: {
              "X-XSRF-TOKEN": xsrfToken,
            },
            credentials: "include",
            throwOnError: true,
          }),
        )
        .catch((e) => {
          setError(`Fout bij uploaden afbeelding: ${e.message ?? String(e)}`);

          setCachedImages((c) =>
            Object.fromEntries(
              Object.entries(c).filter(
                ([key]) => key !== String(residenceTypeId),
              ),
            ),
          );
        })
        .finally(() => fetchResidenceType());
    },
    [setCachedImages, residenceTypeId, fetchResidenceType],
  );

  return (
    <Box w="full">
      <DetailPageHeader>
        {residenceType && (
          <ResidenceTypeActions
            onChange={() => fetchResidenceType()}
            residenceType={residenceType}
          />
        )}
      </DetailPageHeader>
      {loading && <Spinner />}
      {error && <Text color="red.500">{error}</Text>}
      {residenceType && (
        <Box>
          <Heading>{residenceType.name}</Heading>
          <Flex gap={2}>
            <Text>Status:</Text>
            <VisibilityIcon visibility={residenceType.visibility} />
          </Flex>
          {residenceType.hasImage ? (
            <ResidenceTypeImage id={residenceType.id} />
          ) : (
            <Text>Geen afbeelding beschikbaar</Text>
          )}
          <ImageInput
            ref={fileInputRef}
            noConfirm
            onChange={(files) => uploadResidenceTypeImage(files[0])}
          >
            <>
              <AttachmentIcon mr={1} />
              Selecteer afbeelding
            </>
          </ImageInput>
          <AssignedCustomers residenceTypeId={residenceTypeId} />
          <SituationTable residenceTypeId={residenceTypeId} />
        </Box>
      )}
    </Box>
  );
}
